/**
 * The guide — the second branch of Småken. Where to go in each town, what we
 * actually think of the places, and what to order when you get there.
 *
 * Launch scope: Växjö only. Other towns appear as upcoming (see UPCOMING_CITIES).
 */

import { PLACES, Place } from './smaken';

export interface Verdict {
  slug: string;
  /** Our score out of 5, in halves. This is an opinion, not an average. */
  rating: number;
  /** One line that earns the score. */
  verdict: string;
  /** The single thing to order when you get there. */
  order: string;
}

export const VERDICTS: Verdict[] = [
  {
    slug: 'lilla-verket',
    rating: 4.5,
    verdict: 'Mackor som tar fyllning på allvar och sallader som inte är en kompromiss.',
    order: 'Fig & Pig. Och en Basque Cheesecake Pistage till efteråt.',
  },
  {
    slug: 'top-notch-bakery',
    rating: 4.5,
    verdict: 'Ett bageri som löser hela morgonmötet — och sedan fikat på eftermiddagen.',
    order: 'Stora frukostpaketet. Ingefärsshoten är inte förhandlingsbar.',
  },
];

export const verdictFor = (slug: string): Verdict | undefined =>
  VERDICTS.find((v) => v.slug === slug);

export interface CityGuide {
  city: string;
  /** Reads under the city name on the guide card. */
  strap: string;
  intro: string;
  /** A day in the town, told as a few stops. */
  perfectDay: { time: string; what: string; where: string; slug?: string }[];
  image: string;
}

export const CITY_GUIDES: CityGuide[] = [
  {
    city: 'Växjö',
    strap: 'Där vi började. Mackverk, frukostpaket och bakverk med namn',
    intro:
      'Småland i miniatyr. Här finns det mesta inom gångavstånd — och här finns de två ställen vi började med.',
    perfectDay: [
      { time: '08.00', what: 'Stora frukostpaketet', where: 'Top Notch Bakery', slug: 'top-notch-bakery' },
      { time: '12.00', what: 'Fig & Pig eller Beet It', where: 'Lilla Verket', slug: 'lilla-verket' },
      { time: '15.00', what: 'Peanut Prince och en lemonad', where: 'Top Notch Bakery', slug: 'top-notch-bakery' },
      { time: '17.00', what: 'Basque Cheesecake att ta med hem', where: 'Lilla Verket', slug: 'lilla-verket' },
    ],
    image:
      'https://cdn.magicpatterns.com/uploads/caju5vU6ZxpU7hP5vvMVTb/598180901_18089042921511062_8692022983549048154_n..jpg',
  },
];

export const guideFor = (city: string): CityGuide | undefined =>
  CITY_GUIDES.find((g) => g.city.toLowerCase() === city.toLowerCase());

export const placesIn = (city: string): Place[] =>
  PLACES.filter((p) => String(p.city).toLowerCase() === city.toLowerCase());

/** Our top-rated places, best first. */
export const topRated = (limit = 4): { place: Place; verdict: Verdict }[] =>
  VERDICTS.slice()
    .sort((a, b) => b.rating - a.rating)
    .map((verdict) => ({ place: PLACES.find((p) => p.slug === verdict.slug)!, verdict }))
    .filter((row) => Boolean(row.place))
    .slice(0, limit);
